import { useCallback, useEffect, useState } from 'react';
import BillService from '../services/Billservice';
import { notify } from '../components/Customs/NotifyProvider';
import {
  canViewModule,
  canCreateModule,
  canUpdateModule,
  canDeleteModule,
} from '../utils/permissionUtils';

const DEFAULT_GENERAL_SETTINGS = {
  cgst_tax: '0',
  sgst_tax: '0',
  tax_type: 'percentage',
  additional_charge: '0',
  parcel_charge: '0',
  ac_charge: '0',
};

/**
 * Shared bill-management logic (create/update/split/merge/settle bills,
 * totals calc) used by any page that lets staff open a bill panel against
 * a `tables` row — the real dine-in Tables & Bills page, and Room Service
 * (which backs each hotel room with a hidden table row, see
 * roomTableLink.js).
 *
 * `loadTables`, if provided, is called on mount to populate the initial
 * table list (and their active bills) — pass nothing to manage tables
 * yourself and call `registerTable` as they're resolved instead.
 */
export function useBilling({ moduleKey, loadTables } = {}) {
  const [tables, setTables] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeBills, setActiveBills] = useState({});
  const [generalSettings, setGeneralSettings] = useState(DEFAULT_GENERAL_SETTINGS);
  const [loading, setLoading] = useState(false);

  const [billModalState, setBillModalState] = useState({
    open: false,
    tableId: null,
    billId: null,
  });

  const getTableById = useCallback(
    (id) => tables.find((t) => String(t.id) === String(id)) || null,
    [tables]
  );

  const registerTable = useCallback((table) => {
    if (!table?.id) return;

    setTables((prev) => {
      if (prev.some((t) => String(t.id) === String(table.id))) {
        return prev.map((t) =>
          String(t.id) === String(table.id) ? { ...t, ...table } : t
        );
      }
      return [...prev, table];
    });
  }, []);

  const getActiveBillsForTable = useCallback(
    (tableId) => (activeBills[tableId] || []).filter(Boolean),
    [activeBills]
  );

  const getBillTotal = useCallback(
    (bill, table = {}) => {
      if (!bill) return { subtotal: 0, discount: 0, tax: 0, total: 0 };
      if (bill.totals) return bill.totals;

      const items = bill.items || [];
      const getNum = (key) => Number(generalSettings?.[key] || 0);

      const parcelCharge = getNum('parcel_charge');
      const additionalCharge = getNum('additional_charge');
      const acChargeSetting = getNum('ac_charge');

      const subtotal = items.reduce(
        (sum, item) =>
          sum + Number(item.price || 0) * Number(item.qty || item.quantity || 0),
        0
      );

      const discount = Number(bill.discount || 0);

      const parcelQty = items.reduce((sum, item) => {
        return Number(item.is_parcel || 0) === 1
          ? sum + Number(item.qty || item.quantity || 0)
          : sum;
      }, 0);

      const parcelChargeTotal = parcelQty * parcelCharge;
      const additionalChargeTotal = parcelQty * additionalCharge;

      const isAcTable = String(table?.ac_type || '').toUpperCase() === 'AC';
      const acChargeTotal = isAcTable ? acChargeSetting : 0;

      const tax = items.reduce(
        (sum, item) =>
          sum + Number(item.item_cgst_amount || 0) + Number(item.item_sgst_amount || 0),
        0
      );

      const total = Math.max(
        0,
        subtotal + parcelChargeTotal + additionalChargeTotal + acChargeTotal - discount + tax
      );

      return { subtotal, discount, tax, total };
    },
    [generalSettings]
  );

  const refreshTables = useCallback(async () => {
    if (!loadTables) return tables;

    try {
      const data = await loadTables();
      const safeTables = Array.isArray(data) ? data : [];
      setTables(safeTables);
      return safeTables;
    } catch (err) {
      notify.error('Refresh Failed', `Failed to refresh tables: ${err.message}`);
      return [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadTables]);

  const refreshBillsForTable = useCallback(async (tableId) => {
    try {
      const freshBills = await BillService.getBillsByTable(tableId);
      const safeBills = (freshBills || []).filter(Boolean);

      setActiveBills((prev) => ({
        ...prev,
        [tableId]: safeBills,
      }));

      return safeBills;
    } catch (err) {
      notify.error('Refresh Failed', `Refresh bills failed: ${err.message}`);
      return [];
    }
  }, []);

  const loadInitialData = useCallback(async () => {
    if (moduleKey && !canViewModule(moduleKey)) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [menuData, categoriesData, settingsData] = await Promise.all([
        BillService.getAllMenuItems(),
        BillService.getAllCategories(),
        BillService.getGeneralSettings(),
      ]);

      setMenuItems(Array.isArray(menuData) ? menuData : []);
      setCategories(Array.isArray(categoriesData) ? categoriesData : []);
      setGeneralSettings(
        settingsData && typeof settingsData === 'object'
          ? { ...DEFAULT_GENERAL_SETTINGS, ...settingsData }
          : DEFAULT_GENERAL_SETTINGS
      );

      if (loadTables) {
        const tablesData = await loadTables();
        const safeTables = Array.isArray(tablesData) ? tablesData : [];
        setTables(safeTables);

        const occupiedTables = safeTables.filter((t) => t.status === 'occupied');

        if (occupiedTables.length) {
          const billResults = await Promise.all(
            occupiedTables.map((t) => BillService.getBillsByTable(t.id))
          );

          const billMap = {};
          occupiedTables.forEach((t, idx) => {
            billMap[t.id] = (billResults[idx] || []).filter(Boolean);
          });

          setActiveBills(billMap);
        } else {
          setActiveBills({});
        }
      }
    } catch (err) {
      notify.error('Load Failed', `Failed to load billing data: ${err.message}`);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleKey, loadTables]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const createBill = useCallback(
    async (tableId, extraFields = {}) => {
      if (moduleKey && !canCreateModule(moduleKey)) {
        notify.error('Permission Denied', 'No permission to create bill');
        return null;
      }

      try {
        const bill = await BillService.createBill({ table_id: tableId, ...extraFields });

        setActiveBills((prev) => ({
          ...prev,
          [tableId]: [...(prev[tableId] || []), bill].filter(Boolean),
        }));

        const refreshedBills = await refreshBillsForTable(tableId);
        await refreshTables();

        notify.success('Bill Created', 'New bill created');

        if (bill?.id) return bill;
        if (refreshedBills?.length) {
          return refreshedBills[refreshedBills.length - 1];
        }

        return bill;
      } catch (err) {
        notify.error('Create Failed', `Create bill failed: ${err.message}`);
        return null;
      }
    },
    [moduleKey, refreshBillsForTable, refreshTables]
  );

  const deleteBill = useCallback(
    async (tableId, billId) => {
      if (moduleKey && !canDeleteModule(moduleKey)) {
        notify.error('Permission Denied', 'No permission to delete bill');
        return;
      }

      try {
        await BillService.deleteBill(billId);

        setActiveBills((prev) => ({
          ...prev,
          [tableId]: (prev[tableId] || [])
            .filter(Boolean)
            .filter((b) => String(b.id) !== String(billId)),
        }));

        await refreshTables();
        notify.info('Bill Removed', 'Bill removed');
      } catch (err) {
        notify.error('Delete Failed', `Delete bill failed: ${err.message}`);
      }
    },
    [moduleKey, refreshTables]
  );

  const updateBillItems = useCallback(
    async (tableId, billId, items) => {
      if (moduleKey && !canUpdateModule(moduleKey)) {
        notify.error('Permission Denied', 'No permission to update bill items');
        return null;
      }

      try {
        const updatedBill = await BillService.updateBillItems(billId, items);

        setActiveBills((prev) => ({
          ...prev,
          [tableId]: (prev[tableId] || [])
            .filter(Boolean)
            .map((b) => (String(b.id) === String(billId) ? updatedBill : b)),
        }));

        return updatedBill;
      } catch (err) {
        notify.error('Update Failed', `Update items failed: ${err.message}`);
        return null;
      }
    },
    [moduleKey]
  );

  const updateBillMeta = useCallback(
    async (tableId, billId, fields) => {
      if (moduleKey && !canUpdateModule(moduleKey)) {
        notify.error('Permission Denied', 'No permission to update bill');
        return null;
      }

      try {
        const updatedBill = await BillService.updateBillMeta(billId, fields);

        setActiveBills((prev) => ({
          ...prev,
          [tableId]: (prev[tableId] || [])
            .filter(Boolean)
            .map((b) => (String(b.id) === String(billId) ? updatedBill : b)),
        }));

        return updatedBill;
      } catch (err) {
        notify.error('Update Failed', `Update bill failed: ${err.message}`);
        return null;
      }
    },
    [moduleKey]
  );

  const closeBill = useCallback(
    async (tableId, billId, paymentMethod = 'Cash', billSummary = null) => {
      if (moduleKey && !canUpdateModule(moduleKey)) {
        notify.error('Permission Denied', 'No permission to settle bill');
        return;
      }

      try {
        await BillService.settleBill(billId, paymentMethod, billSummary);

        setActiveBills((prev) => ({
          ...prev,
          [tableId]: (prev[tableId] || [])
            .filter(Boolean)
            .filter((b) => String(b.id) !== String(billId)),
        }));

        await refreshTables();
        notify.success('Bill Settled', 'Bill settled successfully');
      } catch (err) {
        notify.error('Settle Failed', `Settle bill failed: ${err.message}`);
      }
    },
    [moduleKey, refreshTables]
  );

  const splitBill = useCallback(
    async (tableId, billId, itemIdsOrObjects) => {
      if (moduleKey && !canUpdateModule(moduleKey)) {
        notify.error('Permission Denied', 'No permission to split bill');
        return;
      }

      if (!itemIdsOrObjects || itemIdsOrObjects.length === 0) {
        notify.warning('Selection Required', 'Select at least one item to split');
        return;
      }

      const itemIds = itemIdsOrObjects
        .map((i) => (typeof i === 'object' ? i.id : i))
        .filter(Boolean);

      if (!itemIds.length) {
        notify.error('Split Failed', 'Could not determine item IDs for split');
        return;
      }

      try {
        const result = await BillService.splitBill(billId, itemIds);
        const originalBill = result?.original ?? result?.originalBill;
        const newBill = result?.split ?? result?.newBill ?? result?.splitBill;

        if (originalBill && newBill) {
          setActiveBills((prev) => {
            const current = prev[tableId] || [];
            const updated = current.map((b) =>
              String(b.id) === String(billId) ? originalBill : b
            );

            if (!updated.find((b) => String(b.id) === String(newBill.id))) {
              updated.push(newBill);
            }

            return { ...prev, [tableId]: updated };
          });
        } else {
          await refreshBillsForTable(tableId);
        }

        notify.success('Bill Split', 'Bill split successfully');

        return { newBill, originalBill };
      } catch (err) {
        notify.error('Split Failed', `Split failed: ${err.message}`);
        await refreshBillsForTable(tableId);
      }
    },
    [moduleKey, refreshBillsForTable]
  );

  const mergeTables = useCallback(
    async (sourceTableId, targetTableId) => {
      if (moduleKey && !canUpdateModule(moduleKey)) {
        notify.error('Permission Denied', 'No permission to merge tables');
        return;
      }

      try {
        await BillService.mergeTables(sourceTableId, targetTableId);

        await Promise.all([
          refreshBillsForTable(sourceTableId),
          refreshBillsForTable(targetTableId),
          refreshTables(),
        ]);

        notify.success('Tables Merged', 'Tables merged successfully');
      } catch (err) {
        notify.error('Merge Failed', `Merge failed: ${err.message}`);
      }
    },
    [moduleKey, refreshBillsForTable, refreshTables]
  );

  const mergeBillsInTable = useCallback(
    async (tableId, sourceBillId, targetBillId) => {
      if (moduleKey && !canUpdateModule(moduleKey)) {
        notify.error('Permission Denied', 'No permission to merge bills');
        return null;
      }

      if (String(sourceBillId) === String(targetBillId)) {
        notify.warning('Invalid Action', 'Cannot merge a bill with itself');
        return null;
      }

      try {
        const mergedBill = await BillService.mergeBills(sourceBillId, targetBillId);

        setActiveBills((prev) => {
          const current = prev[tableId] || [];
          const updated = current
            .filter((b) => String(b.id) !== String(sourceBillId))
            .map((b) => (String(b.id) === String(targetBillId) ? mergedBill : b));

          return { ...prev, [tableId]: updated };
        });

        notify.success('Bills Merged', 'Bills merged successfully');
        return mergedBill;
      } catch (err) {
        notify.error('Merge Failed', `Merge bills failed: ${err.message}`);
        return null;
      }
    },
    [moduleKey]
  );

  return {
    tables,
    menuItems,
    categories,
    generalSettings,
    loading,
    billModalState,
    setBillModalState,
    getTableById,
    registerTable,
    getActiveBillsForTable,
    getBillTotal,
    refreshTables,
    refreshBillsForTable,
    createBill,
    deleteBill,
    updateBillItems,
    updateBillMeta,
    closeBill,
    splitBill,
    mergeTables,
    mergeBillsInTable,
  };
}
